import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import {
  onAuthStateChanged, GoogleAuthProvider, signInWithPopup, signOut,
  createUserWithEmailAndPassword, signInWithEmailAndPassword, updateProfile
} from 'firebase/auth';
import { auth, db } from '../services/firebase';
import { doc, getDoc, setDoc, updateDoc, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { getDeviceId, describeDevice, subscribeToSecuritySettings, setDeviceWorkspace, registerDevice, getPublicIp, logIp } from '../services/deviceService';
import { createWorkspace } from '../services/workspaceService';
import toast from 'react-hot-toast';

// 1. Crear el Contexto
const AuthContext = createContext();

// 2. Crear el Proveedor (Provider)
// ── Sesión única ───────────────────────────────────────────────────────────
// Cada pestaña que inicia sesión escribe su identificador en el documento del
// usuario. Si aparece otro, esta sesión se cierra sola. Está detrás del ajuste
// app_config/security.singleSession para poder activarlo cuando convenga sin
// arriesgar a dejar gente fuera de golpe.
const SESSION_ID = (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : `s-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const claimSession = async (uid) => {
    try {
        const { label } = describeDevice();
        await updateDoc(doc(db, 'users', uid), {
            activeSessionId: SESSION_ID,
            activeSessionAt: serverTimestamp(),
            activeDeviceId: getDeviceId(),
            activeDeviceLabel: label,
        });
    } catch (error) {
        // Que falle no debe impedir usar la app: es un control, no un requisito.
        console.error('No se pudo registrar la sesión:', error);
    }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Ajustes de seguridad en una ref: los consulta el listener del documento de
  // usuario, que no debe volver a suscribirse cada vez que cambian.
  const securityRef = useRef({ singleSession: false });
  const claimedRef = useRef(false);

  useEffect(() => {
    const unsub = subscribeToSecuritySettings((cfg) => { securityRef.current = cfg || {}; });
    return () => unsub();
  }, []);

  useEffect(() => {
    let unsubscribeUserDoc = null;

    // Escuchar cambios de autenticación de Firebase
    const unsubscribe = onAuthStateChanged(auth, async (authUser) => {
      // Limpiar listener anterior del documento de usuario
      if (unsubscribeUserDoc) {
        unsubscribeUserDoc();
        unsubscribeUserDoc = null;
      }

      if (authUser) {
        setLoading(true);
        const userDocRef = doc(db, 'users', authUser.uid);

        // Listener en tiempo real del documento del usuario
        // Así cuando el admin cambia workspaceId/role, se actualiza automáticamente
        unsubscribeUserDoc = onSnapshot(userDocRef, async (snapshot) => {
          if (snapshot.exists()) {
            const userData = snapshot.data();

            let finalData = { ...userData };
            if (authUser.email === 'vcecursos@gmail.com') {
              finalData.role = 'admin';
              finalData.status = 'active';
              if (userData.status !== 'active' || userData.role !== 'admin') {
                await setDoc(userDocRef, {
                  ...userData,
                  role: 'admin',
                  status: 'active'
                });
              }
            }

            // Sesión única: si el documento trae otro identificador, alguien
            // inició sesión con esta cuenta en otro sitio y aquí se cierra.
            if (securityRef.current.singleSession && userData.activeSessionId
                && userData.activeSessionId !== SESSION_ID) {
              toast.error('Tu cuenta se abrió en otro dispositivo');
              signOut(auth);
              return;
            }
            if (!claimedRef.current) {
              claimedRef.current = true;
              claimSession(authUser.uid);
              // El equipo se da de alta al iniciar sesion, no al abrir Horas
              // Extra: si no, quien nunca entra a ese modulo no aparecia nunca
              // en la lista de dispositivos y no habia forma de autorizarlo.
              const perfil = { uid: authUser.uid, email: authUser.email, displayName: authUser.displayName };
              setDeviceWorkspace(authUser.uid).then(async () => {
                await registerDevice(perfil);
                // Y desde qué IP se conecta, para saber si la de la oficina es
                // fija y, más adelante, poder validarla.
                try {
                  const token = await authUser.getIdToken();
                  const ip = await getPublicIp(token);
                  if (ip) await logIp(ip, perfil);
                } catch { /* el registro de IP nunca debe bloquear el login */ }
              });
            }

            setUser({
              uid: authUser.uid,
              email: authUser.email,
              displayName: authUser.displayName,
              photoURL: authUser.photoURL,
              ...finalData
            });
          } else {
            setUser({
              uid: authUser.uid,
              email: authUser.email,
              displayName: authUser.displayName,
              photoURL: authUser.photoURL,
              role: 'user',
              status: 'pending'
            });
          }
          setLoading(false);
        });
      } else {
        setUser(null);
        setLoading(false);
      }
    });

    return () => {
      unsubscribe();
      if (unsubscribeUserDoc) unsubscribeUserDoc();
    };
  }, []);

  const loginWithGoogle = async () => {
    setLoading(true);
    const provider = new GoogleAuthProvider();
    try {
      const result = await signInWithPopup(auth, provider);
      const authUser = result.user;

      // Verificar si el documento del usuario ya existe
      const userDocRef = doc(db, 'users', authUser.uid);
      const userDoc = await getDoc(userDocRef);

      if (!userDoc.exists()) {
        // --- CREACIÓN DE USUARIO EN FIRESTORE ---
        // Es un usuario nuevo, lo creamos con estado "pending"
        
        let initialRole = 'user';
        let initialStatus = 'pending';

        // ¡IMPORTANTE! Asignar rol de admin a tu correo maestro
        if (authUser.email === 'vcecursos@gmail.com') {
          initialRole = 'admin';
          initialStatus = 'active';
          toast.success('¡Bienvenido, Administrador!');
        } else {
          toast.success('¡Registro exitoso! Tu cuenta está pendiente de aprobación.');
        }

        await setDoc(userDocRef, {
          email: authUser.email,
          displayName: authUser.displayName,
          photoURL: authUser.photoURL,
          role: initialRole,
          status: initialStatus,
          createdAt: new Date()
        });
      }
      // El useEffect de onAuthStateChanged se encargará de setear el usuario
      
    } catch (error) {
      console.error('Error en loginWithGoogle:', error);
      toast.error('Error al iniciar sesión con Google');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Registrar empresa con email/contraseña + crear workspace
   */
  const registerWithEmail = async (email, password, displayName, workspaceData) => {
    setLoading(true);
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      const authUser = result.user;

      // Actualizar displayName en Firebase Auth
      await updateProfile(authUser, { displayName });

      // Crear doc de usuario en Firestore
      const userDocRef = doc(db, 'users', authUser.uid);
      await setDoc(userDocRef, {
        email: authUser.email,
        displayName,
        photoURL: null,
        role: 'admin', // Es admin de su propio workspace
        status: 'pending', // Requiere aprobación del super-admin
        workspaceId: authUser.uid,
        isCollaborator: false,
        createdAt: new Date(),
      });

      // Crear workspace
      await createWorkspace(authUser.uid, {
        ...workspaceData,
        ownerEmail: authUser.email,
      });

      toast.success('Empresa registrada. Pendiente de aprobación.');
      return authUser;
    } catch (error) {
      console.error('Error en registerWithEmail:', error);
      if (error.code === 'auth/email-already-in-use') {
        toast.error('Este email ya está registrado');
      } else if (error.code === 'auth/weak-password') {
        toast.error('La contraseña debe tener al menos 6 caracteres');
      } else {
        toast.error('Error al registrar: ' + error.message);
      }
      throw error;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Registrar empresa con Google + crear workspace
   */
  const registerWithGoogle = async (workspaceData) => {
    setLoading(true);
    const provider = new GoogleAuthProvider();
    try {
      const result = await signInWithPopup(auth, provider);
      const authUser = result.user;

      // Verificar si ya existe
      const userDocRef = doc(db, 'users', authUser.uid);
      const userDoc = await getDoc(userDocRef);
      if (userDoc.exists()) {
        toast.error('Esta cuenta ya existe. Usa "Iniciar Sesión" en su lugar.');
        throw new Error('USER_EXISTS');
      }

      // Crear doc de usuario
      await setDoc(userDocRef, {
        email: authUser.email,
        displayName: authUser.displayName,
        photoURL: authUser.photoURL,
        role: 'admin',
        status: 'pending',
        workspaceId: authUser.uid,
        isCollaborator: false,
        createdAt: new Date(),
      });

      // Crear workspace
      await createWorkspace(authUser.uid, {
        ...workspaceData,
        ownerEmail: authUser.email,
      });

      toast.success('Empresa registrada. Pendiente de aprobación.');
      return authUser;
    } catch (error) {
      if (error.message !== 'USER_EXISTS') {
        console.error('Error en registerWithGoogle:', error);
        toast.error('Error al registrar con Google');
      }
      throw error;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Login con email/contraseña (para usuarios existentes)
   */
  const loginWithEmail = async (email, password) => {
    setLoading(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      console.error('Error en loginWithEmail:', error);
      if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
        toast.error('Email o contraseña incorrectos');
      } else {
        toast.error('Error al iniciar sesión');
      }
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    setLoading(true);
    try {
      // Se libera la sesión para que el siguiente inicio no se cierre solo.
      if (user?.uid) {
        try { await updateDoc(doc(db, 'users', user.uid), { activeSessionId: null }); } catch { /* no bloquea el logout */ }
      }
      claimedRef.current = false;
      await signOut(auth);
      setUser(null);
      toast.success('Sesión cerrada');
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
      toast.error('Error al cerrar sesión');
    } finally {
      setLoading(false);
    }
  };

  const value = {
    user,
    loading,
    loginWithGoogle,
    loginWithEmail,
    registerWithEmail,
    registerWithGoogle,
    logout
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// 3. Crear el Hook (esto es lo que importan tus componentes)
export const useAuth = () => {
  return useContext(AuthContext);
};